import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import './Header.css';
import { useAuth } from '../contexts/AuthContext';
import { useUserRole } from '../hooks/useUserRole';
import { urls, getLinkTarget, getLinkRel } from '../config/urls';
import { EXPLORE_SECTIONS, exploreSectionUrl } from '../config/exploreSections';

// In-app links must stay inside the React router. A normal anchor to the
// absolute dashboard URL reloads the page and the session looks logged out.
const AppNavLink = ({ href, className, onClick, current = false, children }) => {
  let to = null;
  if (href.startsWith(urls.app.base)) {
    to = href.slice(urls.app.base.length) || '/';
  } else if (href.startsWith('/')) {
    to = href;
  }

  if (to) {
    return (
      <Link to={to} className={className} onClick={onClick} aria-current={current ? 'page' : undefined}>
        {children}
      </Link>
    );
  }

  return (
    <a
      href={href}
      className={className}
      onClick={onClick}
      target={getLinkTarget(href)}
      rel={getLinkRel(href)}
    >
      {children}
    </a>
  );
};

AppNavLink.propTypes = {
  href: PropTypes.string.isRequired,
  className: PropTypes.string,
  onClick: PropTypes.func,
  current: PropTypes.bool,
  children: PropTypes.node.isRequired
};

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileExploreOpen, setMobileExploreOpen] = useState(false);
  const { currentUser } = useAuth();
  const { hasShippingAccess, hasArtworkManagement, hasAdminAccess, userRole } = useUserRole();
  const location = useLocation();
  const onExplore = location.pathname.startsWith('/explore');
  const activeExplore = onExplore ? new URLSearchParams(location.search).get('type') : null;

  const handleMenuToggle = () => setMenuOpen((open) => !open);
  const handleNavClick = () => {
    setMenuOpen(false);
    setMobileExploreOpen(false);
  };

  // Use centralized URL configuration
  // All URLs are now environment-aware automatically

  return (
    <>
      <div className="announcement-bar">
        <span role="img" aria-label="sparkle">✨</span> Discover, Collect, and Celebrate Emerging Artistry.
      </div>
      <header className="ae-header">
        <div className="ae-header__container">
          <div className="ae-header__left">
            <a href={urls.main.home} className="ae-header__logo-link" target={getLinkTarget(urls.main.home)} rel={getLinkRel(urls.main.home)}>
              <img src="https://cdn.shopify.com/s/files/1/0438/8087/4152/files/art_emerge_logo_transparent.png?v=1745863055" alt="Art Emerge Logo" className="ae-header__logo" />
            </a>
            
            {/* Desktop Navigation */}
            <nav className="ae-header__nav ae-header__nav--desktop">
              <a href={urls.main.home} className="ae-header__nav-link" target={getLinkTarget(urls.main.home)} rel={getLinkRel(urls.main.home)}>Home</a>
              <div className="ae-header__dropdown">
                <button className={`ae-header__nav-link ae-header__dropdown-toggle${onExplore ? ' active' : ''}`}>
                  Explore <span className="ae-header__dropdown-arrow">▼</span>
                </button>
                <div className="ae-header__dropdown-menu">
                  {EXPLORE_SECTIONS.map((section) => (
                    <AppNavLink
                      key={section.id}
                      href={exploreSectionUrl(section.id)}
                      className="ae-header__dropdown-item"
                      current={activeExplore === section.id}
                    >
                      {section.icon} {section.label}
                    </AppNavLink>
                  ))}
                </div>
              </div>

              <div className="ae-header__dropdown">
                <button className="ae-header__nav-link ae-header__dropdown-toggle">
                  Dashboard <span className="ae-header__dropdown-arrow">▼</span>
                </button>
                <div className="ae-header__dropdown-menu">
                  <AppNavLink href={urls.app.dashboard} className="ae-header__dropdown-item">
                    📊 Overview
                  </AppNavLink>
                  
                  {/* Profile - Available to all authenticated users */}
                  {currentUser && (
                    <AppNavLink href={`${urls.app.dashboard}#profile`} className="ae-header__dropdown-item">
                      👤 Profile
                    </AppNavLink>
                  )}
                  
                  {/* Online Gallery - Only for Artists and Galleries */}
                  {['artist', 'gallery', 'admin'].includes(userRole) && (
                    <AppNavLink href={`${urls.app.dashboard}#gallery`} className="ae-header__dropdown-item">
                      🏛️ My Gallery
                    </AppNavLink>
                  )}
                  
                  {/* Shipping - Only for Artists, Galleries, Curators, and Admins */}
                  {hasShippingAccess() && (
                    <AppNavLink href={urls.app.shipping || '/shipping'} className="ae-header__dropdown-item ae-header__dropdown-item--featured">
                      📦 Shipping & Labels
                    </AppNavLink>
                  )}
                  
                  {/* Artwork Management - Only for Artists, Galleries, Curators, and Admins */}
                  {hasArtworkManagement() && (
                    <AppNavLink href={`${urls.app.dashboard}#artworks`} className="ae-header__dropdown-item">
                      🎨 My Artworks
                    </AppNavLink>
                  )}
                  
                  {/* Exhibitions - For Artists, Galleries, and Curators */}
                  {['artist', 'gallery', 'curator'].includes(userRole) && (
                    <AppNavLink href={`${urls.app.dashboard}#exhibitions`} className="ae-header__dropdown-item">
                      🖼️ My Exhibitions
                    </AppNavLink>
                  )}
                  
                  {/* Collection - Only for Collectors */}
                  {userRole === 'collector' && (
                    <AppNavLink href={`${urls.app.dashboard}#collection`} className="ae-header__dropdown-item">
                      ❤️ My Collection
                    </AppNavLink>
                  )}
                  
                  {/* Mentorship - Available to all authenticated users */}
                  {currentUser && (
                    <AppNavLink href={urls.app.mentorship} className="ae-header__dropdown-item">
                      👨‍🏫 Mentorship
                    </AppNavLink>
                  )}
                  
                  {/* Admin Panel - Only for Admins */}
                  {hasAdminAccess() && (
                    <AppNavLink href={urls.app.admin || '/admin'} className="ae-header__dropdown-item ae-header__dropdown-item--admin">
                      🛡️ Admin Panel
                    </AppNavLink>
                  )}
                </div>
              </div>
              
              <AppNavLink href={urls.app.marketplace} className="ae-header__nav-link">Marketplace</AppNavLink>
            </nav>
          </div>

          <div className="ae-header__right">
            {/* Desktop Icons */}
            <div className="ae-header__icons ae-header__icons--desktop">
              <a href={urls.main.search} className="ae-header__icon-btn" aria-label="Search" target={getLinkTarget(urls.main.search)} rel={getLinkRel(urls.main.search)}>
                <img src="https://cdn.shopify.com/s/files/1/0438/8087/4152/files/search-icon-new.svg?v=1747513249" alt="Search" className="ae-header__icon-img" />
              </a>
              
              {/* Shopify Profile Icon - Only for buyers (collectors, curators, admins) */}
              {!['artist', 'gallery'].includes(userRole) && (
                <a href={urls.main.account} className="ae-header__icon-btn" aria-label="Account" target={getLinkTarget(urls.main.account)} rel={getLinkRel(urls.main.account)}>
                  <img src="https://cdn.shopify.com/s/files/1/0438/8087/4152/files/profile-icon-new.svg?v=1747513354" alt="Account" className="ae-header__icon-img" />
                </a>
              )}
              <a href={urls.main.cart} className="ae-header__icon-btn" aria-label="Cart" target={getLinkTarget(urls.main.cart)} rel={getLinkRel(urls.main.cart)}>
                <img src="https://cdn.shopify.com/s/files/1/0438/8087/4152/files/cart-icon-new.svg?v=1747512567" alt="Cart" className="ae-header__icon-img" />
              </a>
            </div>

            {/* Desktop Account Button */}
            <div className="ae-header__account-wrapper ae-header__account-wrapper--desktop">
              <AppNavLink href={urls.app.account} className="ae-header__account-link ae-header__account-link--secondary">Account</AppNavLink>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="ae-header__menu-btn" 
              onClick={handleMenuToggle} 
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
            >
              <span className="ae-header__menu-icon">
                {menuOpen ? (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 6L6 18M6 6L18 18" stroke="#121212" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 12H21M3 6H21M3 18H21" stroke="#121212" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </span>
            </button>
          </div>
        </div>

        {/* Mobile Navigation Overlay */}
        {menuOpen && (
          <div className="ae-header__mobile-overlay" onClick={handleNavClick}>
            <nav className="ae-header__nav ae-header__nav--mobile" onClick={(e) => e.stopPropagation()}>
              <div className="ae-header__mobile-header">
                <img src="https://cdn.shopify.com/s/files/1/0438/8087/4152/files/art_emerge_logo_transparent.png?v=1745863055" alt="Art Emerge Logo" className="ae-header__mobile-logo" />
                <button 
                  className="ae-header__mobile-close" 
                  onClick={handleNavClick}
                  aria-label="Close menu"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 6L6 18M6 6L18 18" stroke="#121212" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
              
              <div className="ae-header__mobile-links">
                <a href={urls.main.home} className="ae-header__nav-link ae-header__nav-link--mobile" onClick={handleNavClick} target={getLinkTarget(urls.main.home)} rel={getLinkRel(urls.main.home)}>Home</a>
                <AppNavLink href={urls.app.dashboard} className="ae-header__nav-link ae-header__nav-link--mobile" onClick={handleNavClick}>Dashboard</AppNavLink>
                <div className={`ae-header__mobile-group${mobileExploreOpen ? ' is-open' : ''}`}>
                  <button
                    type="button"
                    className={`ae-header__nav-link ae-header__nav-link--mobile ae-header__mobile-group-toggle${onExplore ? ' active' : ''}`}
                    aria-expanded={mobileExploreOpen}
                    aria-controls="ae-mobile-explore"
                    onClick={() => setMobileExploreOpen((open) => !open)}
                  >
                    Explore <span className="ae-header__dropdown-arrow">▼</span>
                  </button>
                  {mobileExploreOpen && (
                    <div id="ae-mobile-explore" className="ae-header__mobile-sublinks">
                      {EXPLORE_SECTIONS.map((section) => (
                        <AppNavLink
                          key={section.id}
                          href={exploreSectionUrl(section.id)}
                          className="ae-header__nav-link ae-header__nav-link--mobile ae-header__nav-link--sub"
                          current={activeExplore === section.id}
                          onClick={handleNavClick}
                        >
                          {section.icon} {section.label}
                        </AppNavLink>
                      ))}
                    </div>
                  )}
                </div>
                <AppNavLink href={urls.app.marketplace} className="ae-header__nav-link ae-header__nav-link--mobile" onClick={handleNavClick}>Marketplace</AppNavLink>
                <AppNavLink href={urls.app.mentorship} className="ae-header__nav-link ae-header__nav-link--mobile" onClick={handleNavClick}>Mentorship</AppNavLink>
              </div>

              <div className="ae-header__mobile-actions">
                {/* Mobile Shopify Profile Icon - Only for buyers (collectors, curators, admins) */}
                {!['artist', 'gallery'].includes(userRole) && (
                  <div className="ae-header__mobile-account-section">
                    <AppNavLink href={urls.app.account} className="ae-header__account-link ae-header__account-link--mobile" onClick={handleNavClick}>
                      <img src="https://cdn.shopify.com/s/files/1/0438/8087/4152/files/profile-icon-new.svg?v=1747513354" alt="Account" className="ae-header__icon-img" />
                      <span>Account</span>
                    </AppNavLink>
                  </div>
                )}
                
                <div className="ae-header__mobile-icons">
                  <a href={urls.main.search} className="ae-header__icon-btn ae-header__icon-btn--mobile" aria-label="Search" onClick={handleNavClick} target={getLinkTarget(urls.main.search)} rel={getLinkRel(urls.main.search)}>
                    <img src="https://cdn.shopify.com/s/files/1/0438/8087/4152/files/search-icon-new.svg?v=1747513249" alt="Search" className="ae-header__icon-img" />
                    <span>Search</span>
                  </a>
                  <a href={urls.main.cart} className="ae-header__icon-btn ae-header__icon-btn--mobile" aria-label="Cart" onClick={handleNavClick} target={getLinkTarget(urls.main.cart)} rel={getLinkRel(urls.main.cart)}>
                    <img src="https://cdn.shopify.com/s/files/1/0438/8087/4152/files/cart-icon-new.svg?v=1747512567" alt="Cart" className="ae-header__icon-img" />
                    <span>Cart</span>
                  </a>
                </div>
              </div>
            </nav>
          </div>
        )}
      </header>
    </>
  );
};

export default Header; 